/**
 * Admin shell — trimmed variant of frontend/web's Header/Sidebar pattern:
 * same colors (#008ED3 accent), same spacing idiom, admin nav only.
 */
import type { ReactNode } from 'react';
import { BellRing, FileText, LayoutDashboard, ListChecks, LogOut, Send, Timer, UserCog, User } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { Button } from '@/components/ui/button';

export type AdminPage = 'dashboard' | 'delivery-log' | 'sender-profiles' | 'templates' | 'compose' | 'job-scheduler';

const NAV: { key: AdminPage; label: string; icon: typeof ListChecks }[] = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'delivery-log', label: 'Delivery Log', icon: ListChecks },
  { key: 'sender-profiles', label: 'Sender Profiles', icon: UserCog },
  { key: 'templates', label: 'Templates', icon: FileText },
  { key: 'compose', label: 'Compose', icon: Send },
  { key: 'job-scheduler', label: 'Job Scheduler', icon: Timer },
];

export function AdminShell({
  page,
  onNavigate,
  userName,
  onLogout,
  children,
}: {
  page: AdminPage;
  onNavigate: (p: AdminPage) => void;
  userName: string;
  onLogout: () => void;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* sidebar */}
      <aside className="w-64 shrink-0 bg-[#0F172A] text-white flex flex-col shadow-xl z-10">
        <div className="px-6 py-8 flex items-center gap-3">
          <div className="w-8 h-8 bg-[#008ED3] rounded-lg flex items-center justify-center shadow-lg shadow-[#008ED3]/20">
            <BellRing size={18} className="text-white" />
          </div>
          <span className="font-bold text-[17px] tracking-tight text-white">OM365 Notify</span>
        </div>

        <nav className="flex-1 px-3 space-y-1">
          {NAV.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => onNavigate(key)}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium rounded-lg transition-all duration-200 group",
                page === key
                  ? "bg-[#008ED3] text-white shadow-md shadow-[#008ED3]/10"
                  : "text-slate-400 hover:text-white hover:bg-white/5"
              )}
            >
              <Icon size={16} className={cn(
                "transition-colors",
                page === key ? "text-white" : "text-slate-500 group-hover:text-slate-300"
              )} />
              {label}
            </button>
          ))}
        </nav>

        <div className="p-4 mt-auto">
          <div className="bg-white/5 rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center border border-white/10">
                <User size={14} className="text-slate-300" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-[13px] font-semibold text-white truncate">{userName}</span>
                <span className="text-[11px] text-slate-500 truncate italic">Administrator</span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="w-full justify-start text-slate-400 hover:text-[#EF4444] hover:bg-[#EF4444]/10 h-8 px-2 text-[12px] group shadow-none"
            >
              <LogOut size={14} className="mr-2 group-hover:text-[#EF4444]" />
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      {/* content */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 shrink-0 bg-white border-b border-[#E2E8F0] px-8 flex items-center justify-between sticky top-0 z-10 shadow-sm shadow-black/[0.01]">
          <h1 className="text-lg font-bold text-[#0F172A]">
            {NAV.find((n) => n.key === page)?.label}
          </h1>
          <div className="flex items-center gap-4 text-[12px] text-[#64748B]">
            <span className="flex items-center gap-1.5 px-3 py-1 bg-slate-50 border border-slate-100 rounded-full">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              System Online
            </span>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto px-8 py-8">
          <div className="max-w-6xl mx-auto">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}
